import { Suspense, lazy } from "react";
import { Toaster, SonnerToaster, TooltipProvider } from "@lyder-talk/ui";
import { OneSignalProvider } from "./contexts/OneSignalContext";
import { ContentGenerationProvider } from "./contexts/ContentGenerationContext";
import { AIAssistantProvider } from "./contexts/AIAssistantContext";
import { GamificationToasts, AutoClaimRewards } from "./components/gamification";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import {
  AuthProvider,
  ProfileProvider,
  createQueryClient,
} from "@lyder-talk/core";
import { LanguageProvider } from "./i18n";
import { RequireAuth } from "./components/auth/RequireAuth";

// Layouts
import { AppShell } from "@/layouts/AppShell";
import { PublicLayout } from "@/layouts/PublicLayout";
import { AuthLayout } from "@/layouts/AuthLayout";

// Widgets
import { AIAssistantWidget } from "@/components/ai-assistant/AIAssistantWidget";
import { DictionaryWidget } from "@/components/dictionary/DictionaryWidget";
import { AssistantFab } from "@/components/layout/AssistantFab";
import { StreakDetailHost } from "@/components/gamification";
import { TextSelectionToolbar } from "@/components/selection/TextSelectionToolbar";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { ReviewReminderProvider } from "@/components/review";
import { VersionDetector } from "@/components/VersionDetector";
import { PageTourProvider } from "./contexts/PageTourContext";
import { StudyingNowProvider } from "./contexts/StudyingNowContext";
import { AuthRequiredProvider } from "./contexts/AuthRequiredContext";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";

// Lazy load pages
const MyTopics = lazy(() => import("./pages/MyTopics"));
const MyTopicDetail = lazy(() => import("./pages/MyTopicDetail"));
const MyTopicsAI = lazy(() => import("./pages/MyTopicsAI"));
const Progress = lazy(() => import("./pages/Progress"));
const SharedSession = lazy(() => import("./pages/SharedSession"));
const PracticeSession = lazy(() => import("./pages/PracticeSession"));
const StudyGroups = lazy(() => import("./pages/StudyGroups"));
const StudyGroupDetail = lazy(() => import("./pages/StudyGroupDetail"));
const Topics = lazy(() => import("./pages/Topics"));
const MyVocabulary = lazy(() => import("./pages/MyVocabulary"));
const VocabularyPractice = lazy(() => import("./pages/VocabularyPractice"));
const VocabularyCollections = lazy(
  () => import("./pages/VocabularyCollections"),
);
const VocabularyNotes = lazy(() => import("./pages/VocabularyNotes"));
const VocabularyCollectionDetail = lazy(
  () => import("./pages/VocabularyCollectionDetail"),
);
const CreateVocabularyCollection = lazy(
  () => import("./pages/CreateVocabularyCollection"),
);
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const VideoDetail = lazy(() => import("./pages/VideoDetail"));
const VideoInContext = lazy(() => import("./pages/VideoInContext"));
const Videos = lazy(() => import("./pages/Videos"));
const RandomQuestion = lazy(() => import("./pages/RandomQuestion"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Auth components
import Signin from "./components/auth/Signin";
import Signup from "./components/auth/Signup";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import ConfirmEmail from "./components/auth/ConfirmEmail";
import AcceptInvite from "./components/auth/AcceptInvite";

/**
 * Suspense fallback for lazily-loaded routes.
 *
 * A spinner would flash across the whole shell, so this renders a quiet
 * skeleton shaped like a page: header band, then two content columns. The
 * pulse is `animate-pulse` on blocks that never shift, so the layout stays
 * stable while the chunk arrives.
 */
const PageLoader = () => (
  <div
    role="status"
    aria-busy="true"
    className="container mx-auto px-2 py-4 sm:px-3 sm:py-6 md:pb-6"
  >
    <span className="sr-only">Loading page…</span>
    <div className="animate-pulse space-y-4">
      <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6 lg:p-8 dark:border-stone-700 dark:bg-stone-900">
        <div className="h-4 w-28 rounded-full bg-stone-200/70 dark:bg-stone-700" />
        <div className="mt-4 h-8 w-2/5 max-w-sm rounded-lg bg-stone-200/70 dark:bg-stone-700" />
        <div className="mt-3 h-4 w-3/5 max-w-lg rounded-full bg-stone-100 dark:bg-stone-800" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-44 rounded-2xl border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900"
          />
        ))}
      </div>
    </div>
  </div>
);

const queryClient = createQueryClient();

const AppRoutes = () => {
  const location = useLocation();

  return (
    <Routes>
      {/* Public pages with shared layout */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/topics" element={<Topics />} />
        <Route path="/topics/category/:categoryName" element={<Topics />} />
        <Route path="/topics/:filter" element={<Topics />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/videos" element={<Videos />} />
        {/* Static sibling of /videos/:videoId — declared first so "in-context"
            is never captured as a video id. */}
        <Route path="/videos/in-context" element={<VideoInContext />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/study-groups" element={<StudyGroups />} />
        <Route
          path="/study-groups/:id"
          element={
            <RequireAuth>
              <StudyGroupDetail />
            </RequireAuth>
          }
        />
        {/* Vocabulary routes */}
        <Route
          path="/vocabulary"
          element={
            <RequireAuth>
              <MyVocabulary />
            </RequireAuth>
          }
        />
        <Route
          path="/vocabulary/notes"
          element={
            <RequireAuth>
              <VocabularyNotes />
            </RequireAuth>
          }
        />
        <Route
          path="/vocabulary/collections"
          element={
            <RequireAuth>
              <VocabularyCollections />
            </RequireAuth>
          }
        />
        <Route
          path="/vocabulary/collections/:id"
          element={
            <RequireAuth>
              <VocabularyCollectionDetail />
            </RequireAuth>
          }
        />
        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Vocabulary collection creator — full-height, no footer */}
      <Route element={<PublicLayout showFooter={false} />}>
        <Route
          path="/vocabulary/collections/new"
          element={
            <RequireAuth>
              <CreateVocabularyCollection />
            </RequireAuth>
          }
        />
        <Route
          path="/vocabulary/collections/:id/edit"
          element={
            <RequireAuth>
              <CreateVocabularyCollection />
            </RequireAuth>
          }
        />
      </Route>

      {/* Vocabulary practice - full-height, no footer. The bottom padding stays
          at the layout default: the desk's rating bar is compact enough to
          share the corner with the assistant FAB, exactly as it does in every
          other practice mode. */}
      <Route
        element={
          <PublicLayout
            showFooter={false}
            fullHeight
            mainClassName="flex flex-col flex-1 min-h-0 overflow-hidden"
          />
        }
      >
        <Route
          path="/vocabulary/practice"
          element={
            <RequireAuth>
              <VocabularyPractice />
            </RequireAuth>
          }
        />
      </Route>

      {/* Shared session — full-height frame. The banner strip and the two
          columns own the viewport; only the question column and the right
          rail scroll, so the page itself never does.

          `bg-background` is load-bearing: PublicLayout paints a decorative
          banner image behind `main`, and on a full-height work surface it
          bled through the gaps between cards and through the rail. Opaque
          main hides it — the photo now only appears inside SessionHero's
          own banner strip, where it is the backdrop rather than wallpaper. */}
      <Route
        element={
          <PublicLayout
            showFooter={false}
            showBanners={false}
            fullHeight
            mainClassName="!p-0 flex min-h-0 flex-1 flex-col overflow-hidden bg-background"
          />
        }
      >
        <Route
          path="/session/:title"
          element={<SharedSession key={location.pathname} />}
        />
      </Route>

      {/* Video detail - no footer */}
      <Route element={<PublicLayout showFooter={false} />}>
        <Route path="/videos/:videoId" element={<VideoDetail />} />
      </Route>

      {/* Roll & Talk - no footer. The machine is the page; a sitemap under it
          just made the drill look like the end of the document. */}
      <Route element={<PublicLayout showFooter={false} />}>
        <Route path="/roll-and-talk" element={<RandomQuestion />} />
        {/* The page was renamed; the old path keeps resolving rather than 404ing
            so existing links and bookmarks land on the current URL. */}
        <Route
          path="/random-question"
          element={<Navigate to="/roll-and-talk" replace />}
        />
      </Route>

      {/* Auth routes - nested with AuthLayout */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth" element={<AuthLayout />}>
        <Route index element={<Signin />} />
        <Route path="signin" element={<Signin />} />
        <Route path="signup" element={<Signup />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="confirm" element={<ConfirmEmail />} />
        <Route path="invite" element={<AcceptInvite />} />
      </Route>

      {/* My Topics with sidebar layout */}
      <Route
        path="/my-topics"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<MyTopics />} />
        {/* One component serves both: it reads the optional conversationId and
            renders the new-chat or existing-conversation branch. */}
        <Route path="ai" element={<MyTopicsAI />} />
        <Route path="ai/:conversationId" element={<MyTopicsAI />} />
        {/* Study time + recordings live under one Progress page. The two
            legacy paths still resolve, each opening its matching tab, so old
            links and bookmarks keep working. */}
        <Route path="progress" element={<Progress />} />
        <Route path="time" element={<Progress initialTab="time" />} />
        <Route path="speaking" element={<Progress initialTab="speaking" />} />
        <Route path="edit/:topicId" element={<MyTopicDetail />} />
      </Route>

      {/* Profile redirects */}
      <Route
        path="/profile/:section"
        element={<Navigate to="/my-topics" replace />}
      />
      <Route path="/profile" element={<Navigate to="/my-topics" replace />} />

      {/* Practice session - outside PublicLayout (full-screen experience) */}
      <Route
        path="/session/:title/practice"
        element={
          <RequireAuth>
            <PracticeSession key={location.pathname} />
          </RequireAuth>
        }
      />
    </Routes>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAuthRoute = location.pathname.startsWith("/auth");

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <AppRoutes />
      </Suspense>
      {!isAuthRoute && <AIAssistantWidget />}
      {!isAuthRoute && <DictionaryWidget />}
      {!isAuthRoute && <AssistantFab />}
      {!isAuthRoute && <TextSelectionToolbar />}
      {!isAuthRoute && <MobileBottomNav />}
      {/* Streak detail is store-driven, so it is mounted once here rather than
          in every chrome that happens to render on a given route. See
          `StreakDetailHost` — mounting it per-layout opened two dialogs at
          once on the home page. */}
      <StreakDetailHost />
      <ReviewReminderProvider />
      <VersionDetector />
    </>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <ProfileProvider>
              {/* Inside ProfileProvider on purpose: the language preference
                  lives on the profile row, and reading it here reuses the
                  cached profile instead of firing a second query. */}
              <LanguageProvider>
                <ContentGenerationProvider>
                  <AIAssistantProvider>
                    <OneSignalProvider>
                      <TooltipProvider>
                        <Toaster />
                        <SonnerToaster />
                        <GamificationToasts />
                        {/* Claims completed daily-challenge rewards in the
                            background; it must sit inside the toast layer so a
                            claim can announce itself. */}
                        <AutoClaimRewards />
                        <AuthRequiredProvider>
                          <StudyingNowProvider>
                            <PageTourProvider>
                              <AppContent />
                            </PageTourProvider>
                          </StudyingNowProvider>
                        </AuthRequiredProvider>
                      </TooltipProvider>
                    </OneSignalProvider>
                  </AIAssistantProvider>
                </ContentGenerationProvider>
              </LanguageProvider>
            </ProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
